/**
 * Image Optimization Utilities
 * Helper functions for optimizing images with WebP, responsive sizes, and lazy loading
 */

export interface ImageDimensions {
    width: number;
    height: number;
    aspectRatio: number;
}

/**
 * Optimize Unsplash URL with WebP format and quality settings
 */
export const optimizeUnsplashUrl = (url: string, options?: {
    width?: number;
    quality?: number;
    format?: 'webp' | 'jpg' | 'png';
}): string => {
    if (!url || !url.includes('unsplash.com')) {
        return url;
    }

    const { width, quality = 80, format = 'webp' } = options || {};

    // Parse existing URL
    const urlObj = new URL(url);

    // Set format to WebP for better compression
    urlObj.searchParams.set('fm', format);

    // Set quality
    urlObj.searchParams.set('q', quality.toString());

    // Set width if provided
    if (width) {
        urlObj.searchParams.set('w', width.toString());
    }

    // Ensure fit and auto are set
    urlObj.searchParams.set('fit', 'crop');
    urlObj.searchParams.set('auto', 'format');

    return urlObj.toString();
};

/**
 * Generate srcset for responsive images
 */
export const generateSrcSet = (baseUrl: string, sizes: number[] = [400, 800, 1200]): string => {
    if (!baseUrl || !baseUrl.includes('unsplash.com')) {
        return '';
    }

    return sizes
        .map(size => `${optimizeUnsplashUrl(baseUrl, { width: size })} ${size}w`)
        .join(', ');
};

/**
 * Calculate aspect ratio from dimensions
 */
export const calculateAspectRatio = (width: number, height: number): number => {
    return width / height;
};

/**
 * Get image dimensions from aspect ratio
 */
export const getDimensionsFromAspectRatio = (
    aspectRatio: number,
    maxWidth: number
): ImageDimensions => {
    const width = maxWidth;
    const height = Math.round(maxWidth / aspectRatio);

    return {
        width,
        height,
        aspectRatio
    };
};

/**
 * Preload critical images for better LCP
 */
export const preloadImage = (src: string, as: 'image' = 'image'): void => {
    if (typeof window === 'undefined') return;

    const link = document.createElement('link');
    link.rel = 'preload';
    link.as = as;
    link.href = src;

    // Add to head
    document.head.appendChild(link);
};

/**
 * Common image sizes for responsive images
 */
export const IMAGE_SIZES = {
    thumbnail: [100, 200],
    small: [400, 600],
    medium: [800, 1000],
    large: [1200, 1600],
    hero: [1200, 1600, 2000]
} as const;

/**
 * Common aspect ratios
 */
export const ASPECT_RATIOS = {
    square: 1,
    landscape: 16 / 9,
    portrait: 9 / 16,
    wide: 21 / 9,
    photo: 4 / 3
} as const;

/**
 * Get optimized avatar URL
 */
export const getOptimizedAvatarUrl = (url: string, size: number = 100): string => {
    // Handle pravatar.cc URLs
    if (url.includes('pravatar.cc')) {
        return url;
    }

    // Handle Unsplash URLs
    if (url.includes('unsplash.com')) {
        return optimizeUnsplashUrl(url, { width: size, quality: 85 });
    }

    return url;
};

/**
 * Check if image URL is external
 */
export const isExternalImage = (url: string): boolean => {
    return url.startsWith('http://') || url.startsWith('https://');
};

/**
 * Get blur data URL for placeholder
 */
export const getBlurDataUrl = (width: number = 10, height: number = 10): string => {
    return `data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 ${width} ${height}'%3E%3Cfilter id='b' color-interpolation-filters='sRGB'%3E%3CfeGaussianBlur stdDeviation='1'/%3E%3C/filter%3E%3Crect width='${width}' height='${height}' fill='%23f3f4f6' filter='url(%23b)'/%3E%3C/svg%3E`;
};
